// Path to the introductory logo image

// Import Footer component
import Footer from "./Footer";
// Import chart components from recharts library
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
const introLogo = "/reportImages/intro-logo.png";

/**
 * Generates a summary sentence describing barrage inflow trends.
 * - If all increased: returns a unified message.
 * - If all decreased: same.
 * - If mixed: describes which majority increased or decreased and optionally lists names.
 */
const getBarragesSummaryText = (barragesData) => {
  if (!barragesData?.length) return "";

  const increased = [];
  const decreased = [];

  // Classify barrages by increase/decrease in inflow
  barragesData.forEach((barrage) => {
    const change = barrage.change?.inflow ?? 0;
    if (change > 0) increased.push(barrage.Barrages);
    else if (change < 0) decreased.push(barrage.Barrages);
  });

  const total = barragesData.length;

  // Case 1: All increased
  if (increased.length === total) {
    return "Inflows at all barrages increased.";
  }

  // Case 2: All decreased
  if (decreased.length === total) {
    return "Inflows at all barrages decreased.";
  }

  // Case 3: Mixed scenario
  const majorityIsIncrease = increased.length > decreased.length;

  // Display barrage names or generalize if too many
  const formatLabel = (arr) => {
    if (arr.length > 2) {
      return "most barrages";
    } else if (arr.length === 2) {
      return `${arr[0]} and ${arr[1]}`;
    } else if (arr.length === 1) {
      return arr[0];
    } else {
      return ""; // if empty array
    }
  };

  const incLabel = formatLabel(increased);
  const decLabel = formatLabel(decreased);

  return majorityIsIncrease
    ? `Inflows at ${incLabel} increased while inflows at ${decLabel} decreased this week.`
    : `Inflows at ${decLabel} decreased while inflows at ${incLabel} increased this week.`;
};

/**
 * Highlights the words "increase"/"decrease" in the summary text with custom styles.
 * Wraps them in span with class "increased"/"decreased".
 */
const highlightChangeWord = (text) => {
  // Split into words, spaces, and punctuation
  return text.split(/(\s+|[.,;:!])/).map((part, index) => {
    const clean = part.replace(/[^a-zA-Z]/g, "");
    if (
      clean.toLowerCase() === "increased" ||
      clean.toLowerCase() === "increase"
    ) {
      return (
        <span key={index} className="increased">
          {part}
        </span>
      );
    }
    if (
      clean.toLowerCase() === "decreased" ||
      clean.toLowerCase() === "decrease"
    ) {
      return (
        <span key={index} className="decreased">
          {part}
        </span>
      );
    }
    return part;
  });
};

/**
 * Main component to display barrage summary table and charts.
 */
const BarrageSummary = ({
  data,
  currentWeekLabel,
  previousWeekLabel,
  weekVsWeekLabel,
  currentWeek,
  previousWeek,
}) => {
  // Prepare chart data
  const chartData = data.map((item) => ({
    Barrages: item.Barrages,
    currentWeekInflow: item.currentWeek?.inflow || 0,
    previousWeekInflow: item.previousWeek?.inflow || 0,
    currentWeekOutflow: item.currentWeek?.outflow || 0,
    previousWeekOutflow: item.previousWeek?.outflow || 0,
  }));

  return (
    <>
      {/* Header and Logo */}
      <img src={introLogo} alt="PAR Logo" className="toc-logo" />
      <div style={{ borderBottom: "2px solid #ccc", margin: "0px 40px" }}></div>

      {/* Section Title and Summary Text */}
      <div className="barrage-section" id="barrages-section">
        <h3 className="section-title">3b. Barrages Summary</h3>
        <p className="summary-text">
          {highlightChangeWord(getBarragesSummaryText(data))}
        </p>

        {/* Table: Weekly comparison of inflow/outflow per barrage */}
        <h6 className="toc-heading">Barrages Week on Week Summary</h6>
        <table className="table table-striped comparison-table">
          <thead>
            <tr>
              <th rowSpan={3} style={{ width: "100px" }}>
                Barrages
              </th>
              <th colSpan={2} rowSpan={2} style={{ width: "170px" }}>
                <span>
                  {currentWeekLabel.split("\n").map((line, idx) => (
                    <div key={idx}>
                      {line}
                      <br />
                    </div>
                  ))}
                </span>
              </th>
              <th colSpan={2} rowSpan={2} style={{ width: "170px" }}>
                <span>
                  {previousWeekLabel.split("\n").map((line, idx) => (
                    <div key={idx}>
                      {line}
                      <br />
                    </div>
                  ))}
                </span>
              </th>
              <th colSpan={4}>Week on Week</th>
            </tr>
            <tr>
              <th colSpan={2}>Change</th>
              <th colSpan={2}>%</th>
            </tr>
            <tr>
              <th className="inflow-darkblue">Inflow</th>
              <th className="outflow-darkgreen">Outflow</th>
              <th className="inflow-lightblue">Inflow</th>
              <th className="inflow-lightgreen">Outflow</th>
              <th>Inflow</th>
              <th>Outflow</th>
              <th>Inflow</th>
              <th>Outflow</th>
            </tr>
          </thead>

          <tbody>
            {data.map((b, i) => {
              const { inflow: cIn, outflow: cOut } = b.currentWeek;
              const { inflow: pIn, outflow: pOut } = b.previousWeek;
              const changeIn = b.change?.inflow;
              const changeOut = b.change?.outflow;
              const pctIn = b.percent_change?.inflow;
              const pctOut = b.percent_change?.outflow;

              // Helper function to format values with color for negatives
              const display = (val, isPercent = false) => {
                if (val === null || val === undefined) return "-";
                const num = Number(val);
                if (isNaN(num)) return "-";
                const isNegative = num < 0 || Object.is(num, -0);
                return (
                  <span style={{ color: isNegative ? "#DC2626" : undefined }}>
                    {isNegative ? "-" : ""}
                    {Math.abs(num).toLocaleString(undefined, {
                      minimumFractionDigits: isPercent ? 2 : 0,
                      maximumFractionDigits: isPercent ? 2 : 2,
                    })}
                    {isPercent ? "%" : ""}
                  </span>
                );
              };

              // Helper for neutral formatting (e.g., for raw values)
              const displayNormal = (val) =>
                val === null || val === undefined || val === 0
                  ? "0"
                  : val.toLocaleString();

              return (
                <tr key={i}>
                  <td>{b.Barrages}</td>
                  <td>{displayNormal(cIn)}</td>
                  <td>{displayNormal(cOut)}</td>
                  <td>{displayNormal(pIn)}</td>
                  <td>{displayNormal(pOut)}</td>
                  <td>{display(changeIn)}</td>
                  <td>{display(changeOut)}</td>
                  <td>
                    {pctIn === null || pctIn === undefined
                      ? "-"
                      : display(pctIn, true)}
                  </td>
                  <td>
                    {pctOut === null || pctOut === undefined
                      ? "-"
                      : display(pctOut, true)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p className="source-text">Source: Pakistan Agriculture Research</p>

        {/* =================== CHARTS =================== */}
        <div className="chart-row">
          {/* Inflow Chart */}
          <div className="chart-column">
            <span className="image-caption">
              Barrages Inflow – {weekVsWeekLabel}
            </span>

            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 35 }}>
                {/* X-axis with barrage names */}
                <XAxis
                  dataKey="Barrages"
                  interval={0}
                  fontSize={9}
                  stroke="#000000"
                  strokeWidth={0.5}
                  fontWeight="bold"
                />
                {/* Y-axis with formatted tick labels */}
                <YAxis
                  tickLine={false}
                  fontSize={9}
                  width={40}
                  stroke="#000000"
                  strokeWidth={0.5}
                  fontWeight="bold"
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip />
                <Legend iconSize={8} iconType="circle" fontWeight="bold" />

                {/* Bar for current week inflow */}
                <Bar
                  dataKey="currentWeekInflow"
                  fill="#2F8DCC"
                  name={`${currentWeek} Inflow`}
                >
                  <LabelList
                    dataKey="currentWeekInflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>

                {/* Bar for previous week inflow */}
                <Bar
                  dataKey="previousWeekInflow"
                  fill="#C5DFF1"
                  name={`${previousWeek} Inflow`}
                >
                  <LabelList
                    dataKey="previousWeekInflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Outflow Chart */}
          <div className="chart-column">
            <span className="image-caption">
              Barrages Outflow – {weekVsWeekLabel}
            </span>

            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 35 }}>
                <XAxis
                  dataKey="Barrages"
                  interval={0}
                  fontSize={9}
                  stroke="#000000"
                  strokeWidth={0.5}
                  fontWeight="bold"
                />
                <YAxis
                  tickLine={false}
                  fontSize={9}
                  width={40}
                  stroke="#000000"
                  fontWeight="bold"
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip />
                <Legend iconSize={8} iconType="circle" />

                {/* Bar for current week outflow */}
                <Bar
                  dataKey="currentWeekOutflow"
                  fill="#599859"
                  name={`${currentWeek} Outflow`}
                >
                  <LabelList
                    dataKey="currentWeekOutflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>

                {/* Bar for previous week outflow */}
                <Bar
                  dataKey="previousWeekOutflow"
                  fill="#B7DEB7"
                  name={`${previousWeek} Outflow`}
                >
                  <LabelList
                    dataKey="previousWeekOutflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ marginTop: "250px" }}>
          <Footer footerNumber="6" footerText="Weekly Water & Weather Report" />
        </div>
      </div>
    </>
  );
};

export default BarrageSummary;
